import { publicPreview } from './runtime';

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly requestId: string,
    public readonly detail: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export function apiBase(
  value = import.meta.env.VITE_API_BASE_URL ?? "",
): string {
  const base = value.trim().replace(/\/+$/, "");
  if (!base) return "";
  const parsed = new URL(base);
  if (
    !["http:", "https:"].includes(parsed.protocol) ||
    parsed.username ||
    parsed.password ||
    parsed.search ||
    parsed.hash ||
    parsed.pathname !== "/"
  ) {
    throw new Error(
      "VITE_API_BASE_URL must be an HTTP(S) origin, without a path or credentials.",
    );
  }
  return parsed.origin;
}

function errorMessage(detail: unknown): string {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(errorMessage).join("; ");
  if (detail && typeof detail === "object") {
    const item = detail as Record<string, unknown>;
    if (typeof item.message === "string") return item.message;
    if (typeof item.msg === "string") return item.msg;
  }
  return "The server could not complete this request.";
}

export interface ApiOptions extends RequestInit {
  json?: unknown;
  requestId?: string;
}

/** Transport never retries a paid or state-changing request. */
export async function api<T = unknown>(
  path: string,
  options: ApiOptions = {},
): Promise<T> {
  if (publicPreview) throw new ApiError('The public preview is not connected to the production API.', 0, '', null);
  if (!path.startsWith("/api/") || path.includes("\\") || path.includes(".."))
    throw new Error("Expected an /api/ path.");
  const { json, body, requestId: suppliedId, ...init } = options;
  const headers = new Headers(init.headers);
  const requestId = suppliedId || new Headers(init.headers).get("X-Request-ID") || crypto.randomUUID();
  headers.set("Accept", "application/json");
  headers.set("X-Request-ID", requestId);
  if (json !== undefined) headers.set("Content-Type", "application/json");
  let response: Response;
  try {
    response = await fetch(`${apiBase()}${path}`, {
      ...init,
      headers,
      credentials: "include",
      body: json === undefined ? body : JSON.stringify(json),
    });
  } catch (error) {
    if (
      error &&
      typeof error === "object" &&
      "name" in error &&
      error.name === "AbortError"
    )
      throw error;
    throw new ApiError(
      "Cannot reach Caprica. Check the API address, connection and allowed CORS origin.",
      0,
      requestId,
      null,
    );
  }
  const id = response.headers.get('X-Request-ID') || requestId;
  const text = await response.text();
  let payload: unknown;
  try {
    payload = text ? JSON.parse(text) : null;
  } catch {
    throw new ApiError(
      "The server returned an unexpected response. Check the API address or sign-in gateway.",
      response.status,
      id,
      null,
    );
  }
  if (!response.ok) {
    const detail =
      payload && typeof payload === "object" && "detail" in payload
        ? payload.detail
        : payload;
    throw new ApiError(errorMessage(detail), response.status, id, detail);
  }
  return payload as T;
}

/** One transport for current Studio and retained typed React components. */
export { ApiError as CapricaApiError };
export function apiUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  return `${apiBase()}${path.startsWith("/") ? path : `/${path}`}`;
}
